import useMediaQuery from "@/lib/hooks/use-media-query";
import { cn } from "@/lib/utils";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import {
  createContext,
  useContext,
  type ComponentProps,
  type HTMLAttributes,
} from "react";
import { tv } from "tailwind-variants";

// Context to share breakpoint state across all tooltip components
const TooltipContext = createContext<boolean>(true);

/** Shared surface styles for Radix tooltips and DOM-mounted tooltips (e.g. PII). */
export const tooltipContentClassName =
  "text-foreground z-50 w-fit max-w-xs rounded-md bg-background-neutral-pressed px-3 py-1.5 text-xs text-balance shadow-md";

export const tooltipArrowClassName =
  "z-50 size-2.5 rounded-xs bg-background-neutral-pressed fill-background-neutral-pressed";

// Shared content variant classes
const tooltipContentVariants = tv({
  base: cn(
    tooltipContentClassName,
    "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  ),
  slots: {
    arrow: cn(
      tooltipArrowClassName,
      "translate-y-[calc(-50%-2px)] rotate-45",
    ),
  },
});

// Main Tooltip component that switches based on breakpoint
export default function Tooltip({
  delayDuration = 0,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Root>) {
  const isDesktop = useMediaQuery("md");

  if (isDesktop) {
    return (
      <TooltipContext.Provider value={isDesktop}>
        <TooltipPrimitive.Provider
          key="tooltip"
          data-slot="tooltip-provider"
          delayDuration={delayDuration}
        >
          <TooltipPrimitive.Root data-slot="tooltip" {...props} />
        </TooltipPrimitive.Provider>
      </TooltipContext.Provider>
    );
  }

  return (
    <TooltipContext.Provider value={isDesktop}>
      <PopoverPrimitive.Root key="popover" data-slot="popover" {...props} />
    </TooltipContext.Provider>
  );
}

export function TooltipTrigger({
  asChild,
  className,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Trigger>) {
  const isDesktop = useContext(TooltipContext);

  if (isDesktop) {
    return (
      <TooltipPrimitive.Trigger
        asChild={asChild}
        data-slot="tooltip-trigger"
        className={cn(!asChild && "interactive rounded-md", className)}
        {...props}
      />
    );
  }

  return (
    <PopoverPrimitive.Trigger
      asChild={asChild}
      data-slot="tooltip-trigger"
      className={cn(!asChild && "interactive rounded-md", className)}
      {...props}
    />
  );
}

export function TooltipContent({
  side = "top",
  children,
  className,
  sideOffset = 0,
  ...props
}: ComponentProps<typeof TooltipPrimitive.Content>) {
  const variants = tooltipContentVariants();
  const isDesktop = useContext(TooltipContext);

  if (isDesktop) {
    return (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          data-slot="tooltip-content"
          className={cn(
            variants.base(),
            "origin-(--radix-tooltip-content-transform-origin)",
            className,
          )}
          sideOffset={sideOffset}
          {...props}
        >
          {children}
          <TooltipPrimitive.Arrow
            data-slot="tooltip-arrow"
            className={variants.arrow()}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    );
  }

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        side={side}
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          variants.base(),
          "origin-(--radix-popover-content-transform-origin)",
          className,
        )}
        {...props}
      >
        {children}
        <PopoverPrimitive.Arrow
          data-slot="tooltip-arrow"
          className={variants.arrow()}
        />
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
}

export function TooltipTitle({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("font-semibold", className)} {...props} />;
}

export function TooltipDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-foreground-hovered", className)} {...props} />;
}
